<template>
  <sign-up-modal-shell
    backdrop="scrim"
    :title="isWaitlist ? '' : headerTitle"
    :step-indicator="stepIndicator || ''"
    :card-class="{ 'signup-modal--waitlist': isWaitlist }"
    show-close
    teleport
    @close="close"
  >
    <!-- Header personalizado para la pantalla de waitlist: franja de
         acento + botón de cierre claro en lugar de la barra azul. -->
    <template v-if="isWaitlist" #header>
      <div class="signup-modal__accent"></div>
      <button class="signup-modal__close signup-modal__close--light" @click="close">
        <svg width="16" height="16" viewBox="0 0 22 22" fill="none"><path d="M6 6L16 16M16 6L6 16" stroke="#64748B" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
    </template>

    <!-- Botón "Log In" del header estándar. -->
    <template #header-right>
      <button type="button" class="signup-modal__login" @click="goToLogin">Log In</button>
    </template>

    <!-- Body -->
    <div>

      <!-- Step 1: Role Selection -->
          <div v-if="step === 1">
            <h2 class="signup-modal__title">Choose your role</h2>
            <p class="signup-modal__supporting">Select the option that best describes how you'll use Kiey.</p>

            <div class="signup-modal__roles">
              <div
                class="role-card"
                :class="{ 'role-card--selected': selectedRole === 'agent' }"
                @click="selectRole('agent')"
              >
                <div class="role-card__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 7H4C2.9 7 2 7.9 2 9V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V9C22 7.9 21.1 7 20 7ZM20 19H4V9H20V19ZM14 3H10V5H14V3ZM14 3H10C9.45 3 9 3.45 9 4V5H15V4C15 3.45 14.55 3 14 3Z" fill="#0A317C"/></svg>
                </div>
                <div class="role-card__text">
                  <div class="role-card__title">Real Estate Agent</div>
                  <div class="role-card__desc">I work with homeowners and clients before, during, and after a move.</div>
                </div>
                <div class="role-card__indicator">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
              </div>

              <div
                class="role-card"
                :class="{ 'role-card--selected': selectedRole === 'vendor' }"
                @click="selectRole('vendor')"
              >
                <div class="role-card__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22.7 19L13.6 9.9C14.5 7.6 14 4.9 12.1 3C10.1 1 7.1 0.6 4.7 1.7L8.5 5.5L5.5 8.5L1.7 4.7C0.6 7.1 1 10.1 3 12.1C4.9 14 7.6 14.5 9.9 13.6L19 22.7C19.4 23.1 20 23.1 20.4 22.7L22.6 20.5C23.1 20 23.1 19.3 22.7 19Z" fill="#0A317C"/></svg>
                </div>
                <div class="role-card__text">
                  <div class="role-card__title">Vendor</div>
                  <div class="role-card__desc">I provide home services like plumbing, roofing, electrical, cleaning, and more.</div>
                </div>
                <div class="role-card__indicator">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
              </div>

              <div
                class="role-card"
                :class="{ 'role-card--selected': selectedRole === 'client' }"
                @click="selectRole('client')"
              >
                <div class="role-card__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="#0A317C"/></svg>
                </div>
                <div class="role-card__text">
                  <div class="role-card__title">Homeowner</div>
                  <div class="role-card__desc">I'm a homeowner or resident looking for trusted help with my home.</div>
                </div>
                <div class="role-card__indicator">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
              </div>
            </div>

            <div class="signup-modal__footer">
              <span class="signup-modal__helper">You can change this later if needed.</span>
            </div>
          </div>

          <!-- Step 2: Qualifier (Agent / Vendor) -->
          <div v-if="step === 2">
            <div v-if="qualifierAnswer === null" class="signup-modal__centered">
              <h2 class="signup-modal__title signup-modal__title--center">Are you joining an existing team?</h2>
              <p class="signup-modal__supporting signup-modal__supporting--center" v-if="selectedRole === 'agent'">
                Join your brokerage or team on Kiey.
              </p>
              <p class="signup-modal__supporting signup-modal__supporting--center" v-else>
                Connect with a team that invited you.
              </p>

              <div class="decision-cards">
                <div
                  class="decision-card"
                  :class="{ 'decision-card--selected': qualifierHover === 'yes' }"
                  @click="qualifierAnswer = 'yes'"
                  @mouseenter="qualifierHover = 'yes'"
                  @mouseleave="qualifierHover = null"
                >
                  <span class="decision-card__primary">Yes</span>
                  <span class="decision-card__secondary">My team is on Kiey</span>
                </div>
                <div
                  class="decision-card"
                  :class="{ 'decision-card--selected': qualifierHover === 'no' }"
                  @click="handleQualifierNo"
                  @mouseenter="qualifierHover = 'no'"
                  @mouseleave="qualifierHover = null"
                >
                  <span class="decision-card__primary">No</span>
                  <span class="decision-card__secondary" v-if="selectedRole === 'agent'">I'm creating a new team</span>
                  <span class="decision-card__secondary" v-else>I wasn't invited by a team</span>
                </div>
              </div>

              <div class="signup-modal__footer">
                <button class="signup-btn signup-btn--text" @click="goBack">Back</button>
              </div>
            </div>

            <!-- YES answer: dead end -->
            <div v-if="qualifierAnswer === 'yes'">
              <div class="signup-modal__info-panel">
                <div class="info-panel__icon-wrap">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="#2F6FED"/></svg>
                </div>
                <h3 class="info-panel__title">Contact Your Team Leader</h3>
                <p class="info-panel__text">Please contact your team leader for a Kiey invite link.</p>
                <p class="info-panel__text">Need help? Reach out to <a href="mailto:support@kiey.com" class="info-panel__link">support@kiey.com</a></p>
              </div>

              <div class="signup-modal__footer">
                <button class="signup-btn signup-btn--text" @click="goBack">Back</button>
              </div>
            </div>
          </div>

          <!-- Step 3: Contact Info (plain HTML, no Vuetify / vee-validate) -->
          <div v-if="step === 3">
            <form class="signup-form" novalidate @submit.prevent="goToLocation">
              <div class="signup-form__row">
                <div class="signup-form__field">
                  <label for="signup-first-name" class="signup-form__label">First Name</label>
                  <input
                    id="signup-first-name"
                    v-model="firstName"
                    type="text"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.firstName }"
                    autocapitalize="words"
                    autocorrect="off"
                    spellcheck="false"
                    @blur="validateField('firstName')"
                    @input="clearFieldError('firstName')"
                  />
                  <span v-if="formErrors.firstName" class="signup-form__error">{{ formErrors.firstName }}</span>
                </div>
                <div class="signup-form__field">
                  <label for="signup-last-name" class="signup-form__label">Last Name</label>
                  <input
                    id="signup-last-name"
                    v-model="lastName"
                    type="text"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.lastName }"
                    autocapitalize="words"
                    autocorrect="off"
                    spellcheck="false"
                    @blur="validateField('lastName')"
                    @input="clearFieldError('lastName')"
                  />
                  <span v-if="formErrors.lastName" class="signup-form__error">{{ formErrors.lastName }}</span>
                </div>
              </div>

              <div class="signup-form__row">
                <div class="signup-form__field">
                  <label for="signup-phone" class="signup-form__label">Phone Number</label>
                  <input
                    id="signup-phone"
                    v-model="phoneNumberInput"
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel-national"
                    placeholder="XXX-XXX-XXXX"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.phoneNumber }"
                    @blur="validateField('phoneNumber')"
                    @input="clearFieldError('phoneNumber')"
                  />
                  <span v-if="formErrors.phoneNumber" class="signup-form__error">{{ formErrors.phoneNumber }}</span>
                </div>
                <div class="signup-form__field">
                  <label for="signup-email" class="signup-form__label">Email</label>
                  <input
                    id="signup-email"
                    v-model="email"
                    type="email"
                    autocapitalize="off"
                    autocorrect="off"
                    spellcheck="false"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.email }"
                    @blur="validateField('email')"
                    @input="clearFieldError('email')"
                  />
                  <span v-if="formErrors.email" class="signup-form__error">{{ formErrors.email }}</span>
                </div>
              </div>

              <div v-if="selectedRole === 'vendor'" class="signup-form__row">
                <div class="signup-form__field">
                  <label for="signup-company" class="signup-form__label">Company Name</label>
                  <input
                    id="signup-company"
                    v-model="companyName"
                    type="text"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.companyName }"
                    @blur="validateField('companyName')"
                    @input="clearFieldError('companyName')"
                  />
                  <span v-if="formErrors.companyName" class="signup-form__error">{{ formErrors.companyName }}</span>
                </div>
                <div class="signup-form__field">
                  <label for="signup-service" class="signup-form__label">Service</label>
                  <select
                    id="signup-service"
                    v-model="service"
                    class="signup-form__input signup-form__select"
                    :class="{ 'signup-form__input--error': formErrors.service }"
                    @change="onServiceChange"
                  >
                    <option value="" disabled>Choose your service…</option>
                    <option v-for="cat in sortedServiceCategories" :key="cat" :value="cat">{{ cat }}</option>
                  </select>
                  <input
                    v-if="service === 'Other'"
                    v-model="serviceOther"
                    type="text"
                    placeholder="Type your service"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.service }"
                    style="margin-top: 8px;"
                    @blur="validateField('service')"
                    @input="clearFieldError('service')"
                  />
                  <span v-if="formErrors.service" class="signup-form__error">{{ formErrors.service }}</span>
                </div>
              </div>

              <div class="signup-form__row signup-form__row--full">
                <div class="signup-form__field">
                  <label for="signup-address" class="signup-form__label">Address</label>
                  <GmapAutocomplete
                    id="signup-address"
                    placeholder="Start typing your street address…"
                    @place_changed="onPlaceChangedInline"
                    :options="{ types: ['address'], componentRestrictions: { country: ['us', 'ca'] } }"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.address }"
                  ></GmapAutocomplete>
                  <span v-if="formErrors.address" class="signup-form__error">{{ formErrors.address }}</span>
                  <p v-else-if="locationName" class="signup-form__location-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#19A99D"/></svg>
                    {{ locationName }}
                  </p>
                </div>
              </div>

              <div v-if="selectedRole === 'client'" class="signup-form__row signup-form__row--full">
                <div class="signup-form__field">
                  <label for="signup-heard-from" class="signup-form__label">How did you hear about Kiey?</label>
                  <select
                    id="signup-heard-from"
                    v-model="hearAboutSource"
                    class="signup-form__input signup-form__select"
                    :class="{ 'signup-form__input--error': formErrors.hearAbout }"
                    @change="onHearAboutChange"
                  >
                    <option value="" disabled>Choose one…</option>
                    <option v-for="opt in hearAboutOptions" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <input
                    v-if="hearAboutSource === 'Other'"
                    v-model="hearAboutOther"
                    type="text"
                    placeholder="Tell us how you heard about us"
                    class="signup-form__input"
                    :class="{ 'signup-form__input--error': formErrors.hearAbout }"
                    style="margin-top: 8px;"
                    @blur="validateField('hearAbout')"
                    @input="clearFieldError('hearAbout')"
                  />
                  <span v-if="formErrors.hearAbout" class="signup-form__error">{{ formErrors.hearAbout }}</span>
                </div>
              </div>

              <consent-checkboxes
                :sms-consent.sync="smsConsent"
                :email-consent.sync="emailConsent"
              />

              <div class="signup-modal__footer">
                <button type="button" class="signup-btn signup-btn--text" @click="goBack">Back</button>
                <div style="flex: 1;"></div>
                <button
                  type="submit"
                  class="signup-btn signup-btn--primary signup-btn--lg"
                  :disabled="!smsConsent || !emailConsent"
                >Continue</button>
              </div>
            </form>
          </div>

          <!-- Step 4: Result -->
          <div v-if="step === 4">
            <div v-if="status === 'loading'" class="signup-modal__result">
              <v-progress-circular indeterminate color="#2F6FED" size="48"></v-progress-circular>
              <p class="signup-modal__supporting" style="margin-top: 20px;">Searching for a team in your area...</p>
            </div>

            <!-- Matched (CLIENT or VENDOR): pick a team. Both roles now see
                 a picker before the confirmation so the user always chooses
                 which team they're joining/requesting. -->
            <div v-else-if="result && result.match && (selectedRole === 'client' || selectedRole === 'vendor') && !addedTenantId" class="signup-modal__result signup-modal__result--picker">
              <h3 class="result__title result__title--compact">
                <span v-if="selectedRole === 'client'">You're in Luck!</span>
                <span v-else>Request to Join a Local Team</span>
              </h3>
              <p class="result__text result__text--compact">
                <span v-if="selectedRole === 'client'">
                  <span v-if="result.matches && result.matches.length === 1">There's a team serving your area — tap to join.</span>
                  <span v-else>{{ result.matches.length }} teams serving your area. Pick one to join.</span>
                </span>
                <span v-else>
                  <span v-if="result.matches && result.matches.length === 1">1 team in your area. Request access to get started.</span>
                  <span v-else>{{ result.matches.length }} teams in your area. Choose one to request access.</span>
                </span>
              </p>

              <p v-if="selectTeamError" class="team-picker__error">{{ selectTeamError }}</p>
              <div class="team-picker">
                <button
                  v-for="m in (result.matches || [])"
                  :key="m.id"
                  class="team-picker__card"
                  :disabled="addingTenantId"
                  :class="{ 'team-picker__card--loading': addingTenantId === m.id }"
                  @click="selectTeam(m.id)"
                >
                  <div class="team-picker__card-main">
                    <div class="team-picker__name">{{ m.company_name || 'Kiey Team' }}</div>
                    <div class="team-picker__meta">
                      <span v-if="m.city">{{ m.city }}<span v-if="m.state">, {{ m.state }}</span><span v-if="m.distance_miles != null"> · </span></span>
                      <span v-if="m.distance_miles != null">{{ m.distance_miles }} mi away</span>
                    </div>
                    <div class="team-picker__primary-stat">{{ m.vendor_count }} Vendor{{ m.vendor_count !== 1 ? 's' : '' }} in Network</div>
                    <div class="team-picker__secondary-stat">Led by a Local Agent</div>
                  </div>
                  <svg v-if="addingTenantId !== m.id" width="20" height="20" viewBox="0 0 24 24" fill="none" class="team-picker__chevron"><path d="M9 5l7 7-7 7" stroke="#2F6FED" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  <v-progress-circular v-else indeterminate color="#2F6FED" size="20" width="2"></v-progress-circular>
                </button>
              </div>
            </div>

            <!-- CLIENT: confirmed added -->
            <div v-else-if="result && result.match && selectedRole === 'client' && addedTenantId" class="signup-modal__result signup-modal__result--congrats">
              <div class="result-icon result-icon--success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#19A99D"/></svg>
              </div>
              <h3 class="result__title">You're In — Welcome to Kiey!</h3>
              <p class="result__text">
                You've been added to <strong>{{ addedCompanyName }}</strong>. Download the Kiey app on a mobile device to get started.
              </p>
              <div class="congrats-stores">
                <a :href="appleStoreUrl" target="_blank" rel="noopener" class="congrats-stores__btn">
                  <img :src="require('@/assets/img/apple_store.svg')" alt="Download on the App Store"/>
                </a>
                <a :href="googlePlayUrl" target="_blank" rel="noopener" class="congrats-stores__btn">
                  <img :src="require('@/assets/img/google_play.svg')" alt="Get it on Google Play"/>
                </a>
              </div>
            </div>

            <!-- VENDOR: request received (after tenant picked) -->
            <div v-else-if="result && result.match && selectedRole === 'vendor' && addedTenantId" class="signup-modal__result signup-modal__result--vendor-ack">
              <div class="result-icon result-icon--success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#19A99D"/></svg>
              </div>
              <h3 class="result__title">You're All Set</h3>
              <p class="result__text">
                Your request to join <strong>{{ addedCompanyName }}</strong> has been sent. The team has been notified and will contact you soon.
              </p>
              <p class="result__text result__text--support">
                Questions? Email <a href="mailto:support@kiey.com" class="result__link">support@kiey.com</a>
              </p>
            </div>

            <!-- No match: polished waitlist (premium spec) -->
            <div v-else-if="result && !result.match" class="waitlist-v2">
              <p class="waitlist-v2__eyebrow">Waitlist Confirmed</p>
              <div class="waitlist-v2__icon">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="#2563EB"/></svg>
              </div>
              <h2 class="waitlist-v2__title">You're on the Waitlist</h2>
              <p class="waitlist-v2__body">Kiey isn't available in your area yet, but your spot is saved. We'll email you as soon as local access opens.</p>
              <p v-if="email" class="waitlist-v2__email">Confirmation sent to <a :href="'mailto:' + email">{{ email }}</a></p>
              <div class="waitlist-v2__divider"></div>
              <div class="waitlist-v2__cta-row">
                <button class="waitlist-v2__cta" @click="close">Return to Login</button>
              </div>
            </div>

            <div v-else-if="error" class="signup-modal__result">
              <div class="result-icon result-icon--error">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#DC3545"/></svg>
              </div>
              <h3 class="result__title">Something Went Wrong</h3>
              <p class="result__text">{{ error }}</p>
            </div>

            <!-- Return-to-Login button:
                 - vendor AFTER they've picked a team (request-received ack)
                 - any error screen
                 Hidden on both pickers, the client success (has app-store CTAs),
                 and the waitlist screen (has its own CTA). -->
            <div class="signup-modal__footer" v-if="!isWaitlist && ((result && selectedRole === 'vendor' && addedTenantId) || error)">
              <div style="flex: 1;"></div>
              <button class="signup-btn signup-btn--primary signup-btn--lg" @click="close">Return to Login</button>
            </div>
          </div>

    </div>
  </sign-up-modal-shell>
</template>

<script>
import { mask } from "vue-the-mask";
import { mapState, mapMutations } from "vuex";
import SignUpModalShell from "./components/SignUpModalShell.vue";
import ConsentCheckboxes from "@/components/forms/ConsentCheckboxes";
import { HEAR_ABOUT_OPTIONS } from "@/util/signup_options";

const SERVICE_CATEGORIES = [
  'Plumbing', 'Electrical', 'HVAC', 'Roofing', 'Painting', 'Flooring',
  'Drywall', 'Carpentry', 'Landscaping', 'Lawn Care', 'Home Inspection',
  'Garage Door Services', 'Pest Control', 'Locksmith', 'Appliance Repair',
  'Cleaning Services', 'Carpet Cleaning', 'Handyman Services',
  'Fireplace Services', 'Pool Construction', 'Fencing', 'Concrete',
  'Grading', 'Excavation', 'Radon Mitigation', 'Restoration Services',
  'Home Builder', 'Renovations', 'Insurance', 'Mortgage Brokerage',
  'Property Appraisal', 'Attorney', 'Trucking & Hauling', 'Masonry',
  'Septic Systems', 'Drain & Sewer Services', 'Smart Home Installation',
  'Blinds & Window Coverings', 'Pet Sitting', 'Audio Visual Installation',
  'Alarm System Installation', 'Interior Design', 'Photography',
  'Moving Services', 'Junk Removal', 'Irrigation', 'Well Services',
  'Window Cleaning', 'Air Duct Cleaning', 'Other',
];

export default {
  name: "SignUpWizard",
  components: {
    "sign-up-modal-shell": SignUpModalShell,
    ConsentCheckboxes,
  },
  directives: { mask },
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      step: 1,
      selectedRole: null,
      qualifierAnswer: null,
      qualifierHover: null,
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      companyName: "",
      service: "",
      serviceOther: "",
      hearAboutSource: "",
      hearAboutOther: "",
      hearAboutOptions: HEAR_ABOUT_OPTIONS,
      smsConsent: false,
      emailConsent: false,
      location: null,
      locationName: "",
      serviceCategories: SERVICE_CATEGORIES,
      utm: {},
      addingTenantId: null,
      addedTenantId: null,
      addedCompanyName: '',
      selectTeamError: null,
      formErrors: {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        email: null,
        companyName: null,
        service: null,
        hearAbout: null,
        address: null,
      },
    };
  },
  mounted() {
    // El teleport a document.body lo maneja ahora SignUpModalShell
    // (prop `teleport`) — necesario para que position: fixed cubra el
    // viewport (v-main crea un bloque contenedor).
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
      const val = params.get(key);
      if (val) utm[key] = val;
    });
    if (Object.keys(utm).length) this.utm = utm;
    // signup=client: skip role selection and go straight to contact info
    // signup=vendor: skip role selection, land on the team-picker step
    // signup=agent:  skip role selection, land on the team-picker step
    const presetRole = params.get('signup');
    if (presetRole === 'client') {
      this.selectedRole = 'client';
      this.setRole('client');
      this.step = 3;
    } else if (presetRole === 'vendor' || presetRole === 'agent') {
      this.selectedRole = presetRole;
      this.setRole(presetRole);
      this.step = 2;
    }
  },
  computed: {
    ...mapState({
      status: state => state.Self_signup.status,
      result: state => state.Self_signup.result,
      error: state => state.Self_signup.error,
    }),
    canGoBack() {
      if (this.step === 1) return false;
      if (this.step === 4) return false;
      return true;
    },
    stepIndicator() {
      return null;
    },
    // Título de la barra azul del header — antes estaba inline en el
    // template con v-if por paso; centralizado acá para pasarlo al shell.
    headerTitle() {
      if (this.step === 1) return 'Sign Up for Kiey';
      if (this.step === 2) return this.selectedRole === 'agent' ? 'Real Estate Agent' : 'Vendor';
      if (this.step === 3) return 'Create Your Account';
      if (this.step === 4) return this.resultTitle;
      return '';
    },
    isWaitlist() {
      return this.step === 4 && this.result && !this.result.match;
    },
    appleStoreUrl() {
      return this.$store.getters['Authenticate/getAppleStoreUrl'];
    },
    googlePlayUrl() {
      return this.$store.getters['Authenticate/getGooglePlayUrl'];
    },
    // Alphabetized service list with "Other" pinned to the bottom so it's
    // always the last option — users expect it there as an escape hatch.
    sortedServiceCategories() {
      const list = this.serviceCategories.filter(c => c !== 'Other').slice().sort();
      if (this.serviceCategories.includes('Other')) list.push('Other');
      return list;
    },
    phoneNumberInput: {
      get() {
        return this.phoneNumber;
      },
      set(value) {
        let digits = String(value == null ? '' : value).replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
        if (digits.length > 10) digits = digits.slice(-10);
        let formatted = digits;
        if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
        this.phoneNumber = formatted;
      },
    },
    resultTitle() {
      if (this.status === 'loading') return 'Searching…';
      // Client confirmed added → "Welcome to Kiey!"; Vendor confirmed → "Request Received"
      if (this.addedTenantId) {
        return this.selectedRole === 'vendor' ? 'Request Received' : 'Welcome to Kiey!';
      }
      // Picker-stage headers
      if (this.result && this.result.match && this.selectedRole === 'client') return 'You\'re in Luck!';
      if (this.result && this.result.match && this.selectedRole === 'vendor') return 'Local Teams';
      if (this.result && !this.result.match) return 'You\'re on the Waitlist';
      if (this.error) return 'Oops';
      return '';
    },
  },
  methods: {
    ...mapMutations('Self_signup', ['reset', 'setRole', 'setLocation']),

    selectRole(role) {
      this.selectedRole = role;
      this.setRole(role);
      if (role === 'client') {
        this.step = 3;
      } else {
        this.step = 2;
      }
    },

    handleQualifierNo() {
      if (this.selectedRole === 'agent') {
        // Agents now start at /my-market — they draw their territory and
        // see the price before being routed into account info at /sign_up.
        // Use hard navigation so the SignUpWizard's teleport + body class
        // cleanup has a chance to finish before the new route mounts — a
        // SPA push that races with this.close() was leaving the wizard's
        // overlay on top of the next page, making it look blank.
        window.location.href = `${window.location.origin}/my-market`;
      } else {
        this.step = 3;
      }
    },

    validateField(field) {
      const raw = this[field];
      const value = typeof raw === 'string' ? raw.trim() : raw;
      let error = null;

      switch (field) {
        case 'firstName':
          if (!value) error = 'First name is required';
          break;
        case 'lastName':
          if (!value) error = 'Last name is required';
          break;
        case 'phoneNumber': {
          if (!value) {
            error = 'Phone number is required';
          } else {
            const digits = String(value).replace(/\D/g, '');
            if (digits.length !== 10) error = 'Enter a 10-digit phone number';
          }
          break;
        }
        case 'email':
          if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = 'Enter a valid email address';
          }
          break;
        case 'companyName':
          if (this.selectedRole === 'vendor' && !value) error = 'Company name is required';
          break;
        case 'service':
          if (this.selectedRole === 'vendor') {
            if (!value) error = 'Please choose a category';
            else if (value === 'Other' && !((this.serviceOther || '').trim())) error = 'Type your service';
          }
          break;
        case 'hearAbout':
          if (this.selectedRole === 'client') {
            const pick = (this.hearAboutSource || '').trim();
            if (!pick) error = 'Please tell us how you heard about us';
            else if (pick === 'Other' && !((this.hearAboutOther || '').trim())) error = 'Tell us how you heard about us';
          }
          break;
      }

      this.$set(this.formErrors, field, error);
      return !error;
    },

    clearFieldError(field) {
      if (this.formErrors[field]) this.$set(this.formErrors, field, null);
    },

    onServiceChange() {
      // Switching away from "Other" wipes the free-text fallback so a
      // stale value doesn't get sent on submit.
      if (this.service !== 'Other') this.serviceOther = '';
      this.validateField('service');
    },

    onHearAboutChange() {
      if (this.hearAboutSource !== 'Other') this.hearAboutOther = '';
      this.validateField('hearAbout');
    },

    validateContactForm() {
      const fields = ['firstName', 'lastName', 'phoneNumber', 'email'];
      if (this.selectedRole === 'vendor') fields.push('companyName', 'service');
      if (this.selectedRole === 'client') fields.push('hearAbout');
      let allValid = true;
      for (const field of fields) {
        if (!this.validateField(field)) allValid = false;
      }
      return allValid;
    },

    async goToLocation() {
      if (!this.validateContactForm()) return;
      if (!this.smsConsent || !this.emailConsent) return;
      if (!this.location || this.formErrors.address) {
        this.$set(this.formErrors, 'address',
          this.formErrors.address || 'Please pick a complete address from the dropdown.');
        return;
      }
      this.submitSignup();
    },

    extractPlaceData(place) {
      if (!place || !place.geometry) {
        this.$set(this.formErrors, 'address', 'Please pick an address from the dropdown.');
        return;
      }

      const components = {};
      if (place.address_components) {
        for (const comp of place.address_components) {
          for (const t of comp.types) {
            components[t] = { short: comp.short_name, long: comp.long_name };
          }
        }
      }

      // Postal code is our single-source-of-truth for "this is a real
      // street address" — cities and country-level picks don't carry one,
      // while range-interpolated street matches can miss street_number.
      if (!components.postal_code) {
        this.location = null;
        this.locationName = '';
        this.setLocation(null);
        this.$set(this.formErrors, 'address',
          'Please pick a complete street address from the dropdown (with a postal/zip code).');
        return;
      }

      this.$set(this.formErrors, 'address', null);
      this.location = {
        address: place.formatted_address || place.name,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        street_number: components.street_number ? components.street_number.long : '',
        postal_code: components.postal_code.long,
        city:
          (components.locality && components.locality.long) ||
          (components.sublocality_level_1 && components.sublocality_level_1.long) ||
          '',
        state: (components.administrative_area_level_1 && components.administrative_area_level_1.short) || '',
        country: (components.country && components.country.short) || '',
      };
      this.locationName = place.formatted_address || place.name;
      this.setLocation(this.location);
    },

    onPlaceChangedInline(place) {
      this.extractPlaceData(place);
    },

    async selectTeam(tenantId) {
      if (!this.result || !this.result.request_id || this.addingTenantId) return;
      this.addingTenantId = tenantId;
      this.selectTeamError = null;
      try {
        const added = await this.$store.dispatch('Self_signup/selectTeam', {
          request_id: this.result.request_id,
          tenant_id: tenantId,
        });
        this.addedTenantId = tenantId;
        this.addedCompanyName = (added && added.company_name)
          || (this.result.matches || []).find(m => m.id === tenantId)?.company_name
          || 'your team';
      } catch (err) {
        console.error('[SignUp] select team failed:', err);
        this.selectTeamError = (typeof err === 'string')
          ? err
          : (err && err.message) || 'Could not add you to that team. Please try another.';
      } finally {
        this.addingTenantId = null;
      }
    },

    async submitSignup() {
      if (!this.location) return;
      this.step = 4;
      this.addedTenantId = null;
      this.addedCompanyName = '';

      const data = {
        role: this.selectedRole,
        first_name: this.firstName,
        last_name: this.lastName,
        phone_number: this.phoneNumber.replace(/[\s-]/g, ''),
        email: this.email,
        company_name: this.companyName,
        // When the user picks "Other", the free-text value they typed is
        // what we store — not the literal "other".
        service: this.service === 'Other'
          ? (this.serviceOther || '').trim().toLowerCase()
          : (this.service ? this.service.toLowerCase() : ''),
        location: this.location,
        sms_consent: this.smsConsent,
        email_consent: this.emailConsent,
        utm: Object.keys(this.utm).length ? this.utm : undefined,
      };

      // Clients pick their channel from a dropdown; "Other" stores whatever
      // free text they typed instead of the literal label.
      if (this.selectedRole === 'client') {
        data.signup_heard_from = this.hearAboutSource === 'Other'
          ? (this.hearAboutOther || '').trim()
          : this.hearAboutSource;
      }

      try {
        await this.$store.dispatch('Self_signup/submitSignup', data);
      } catch (err) {
        // Error handled by store
      }
    },

    goBack() {
      if (this.step === 2) {
        if (this.qualifierAnswer === 'yes') {
          this.qualifierAnswer = null;
          return;
        }
        this.selectedRole = null;
        this.step = 1;
      } else if (this.step === 3) {
        if (this.selectedRole === 'client') {
          this.step = 1;
        } else {
          this.qualifierAnswer = null;
          this.step = 2;
        }
      }
    },

    close() {
      this.step = 1;
      this.selectedRole = null;
      this.qualifierAnswer = null;
      this.qualifierHover = null;
      this.firstName = "";
      this.lastName = "";
      this.phoneNumber = "";
      this.email = "";
      this.companyName = "";
      this.service = "";
      this.serviceOther = "";
      this.smsConsent = false;
      this.emailConsent = false;
      this.location = null;
      this.locationName = "";
      this.addingTenantId = null;
      this.addedTenantId = null;
      this.addedCompanyName = "";
      this.selectTeamError = null;
      this.formErrors = {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        email: null,
        companyName: null,
        service: null,
        address: null,
      };
      this.reset();
      this.$emit('update:show', false);
    },

    // Breaks the user out of the iframe (if we're embedded on kiey.com)
    // and lands them on the Kiey login page. Works the same way when the
    // wizard is opened as a modal inside the login view — since
    // window.top === window there, it's just an in-page navigation.
    goToLogin() {
      const loginUrl = `${window.location.origin}/login`;
      try {
        window.top.location.href = loginUrl;
      } catch (e) {
        window.location.href = loginUrl;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/*
 * El shell (SignUpModalShell) aporta el overlay, la card de 520px, el
 * header azul, el body desplazable y los design tokens --kiey-*. Los
 * estilos compartidos de campos / consentimiento / botones / footer /
 * result vienen de signup-form.scss. Acá quedan sólo los estilos
 * propios del wizard (role cards, decision cards, team picker, waitlist,
 * info panel, etc.).
 */
@import "./components/signup-form.scss";

/* ── Header — botón "Log In" (va en el slot header-right del shell) ── */
.signup-modal__login {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #FFFFFF;
  font-weight: 600;
  font-size: 13px;
  line-height: 1;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 180ms ease, transform 180ms ease;
  font-family: inherit;
}

.signup-modal__login:hover {
  background: rgba(255, 255, 255, 0.22);
  transform: translateY(-1px);
}

/* ── Typography ─────────────────────────────────────────── */
.signup-modal__title {
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: #17326b;
  margin: 0 0 6px;
}

.signup-modal__title--center {
  text-align: center;
  font-size: 22px;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin-bottom: 8px;
}

.signup-modal__supporting {
  margin-top: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: #5f6f91;
  margin: 0 0 18px;
  max-width: 440px;
}

.signup-modal__supporting--center {
  text-align: center;
  margin: 0 auto 20px;
  color: #617292;
}

.signup-modal__centered {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Role cards (Step 1) ────────────────────────────────── */
.signup-modal__roles {
  margin-top: 0;
  display: grid;
  gap: 10px;
}

.role-card {
  background: #FFFFFF;
  border: 1px solid #dbe3f0;
  border-radius: 14px;
  padding: 12px 14px;
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease;
  user-select: none;
}

.role-card:hover {
  border-color: #c7d5ee;
  box-shadow: 0 6px 16px rgba(29, 55, 115, 0.06);
  transform: translateY(-1px);
}

.role-card--selected {
  border-color: #2f6fed;
  background: linear-gradient(180deg, #FFFFFF 0%, #f7faff 100%);
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.10);
}

.role-card__icon {
  width: 44px; height: 44px; min-width: 44px;
  border-radius: 12px;
  background: linear-gradient(180deg, #eef3ff 0%, #e8eefb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #1f4598;
}

.role-card__icon svg {
  width: 22px;
  height: 22px;
}

.role-card__icon svg path {
  fill: #1f4598;
}

.role-card__text { flex: 1; min-width: 0; display: flex; flex-direction: column; }

.role-card__title {
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
  color: #17326b;
  margin-bottom: 4px;
}

.role-card__desc {
  margin-top: 0;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.4;
  color: #5f6f91;
}

.role-card__indicator {
  display: none !important;
}

/* Footer divider + helper on step 1 */
.signup-modal__footer.signup-modal__footer--helper {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e3e9f4;
}

/* ── Decision cards (Step 2) ────────────────────────────── */
.decision-cards {
  margin-top: 4px;
  display: flex;
  gap: 10px;
  width: 100%;
}

.decision-card {
  flex: 1;
  min-height: 84px;
  border-radius: 14px;
  border: 1px solid #dbe3f0;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  padding: 14px 12px;
  text-align: center;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease;
  user-select: none;
}

.decision-card:hover {
  border-color: #c8d6ed;
  box-shadow: 0 6px 16px rgba(25, 52, 110, 0.06);
  transform: translateY(-1px);
}

.decision-card--selected {
  border-color: #2f6fed;
  background: linear-gradient(180deg, #FFFFFF 0%, #f7faff 100%);
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.10);
}

.decision-card__primary {
  font-weight: 700;
  font-size: 18px;
  line-height: 1.1;
  color: #17326b;
  margin-bottom: 4px;
}

.decision-card__secondary {
  margin-top: 0;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.35;
  color: #617292;
}

/* Los estilos de .signup-form__* y .consent-* viven ahora en
   signup-form.scss (campos) y ConsentCheckboxes.vue (consentimiento). */

/* ── Location input (Step 4) ────────────────────────────── */
.signup-modal__location-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-input);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-primary);
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  margin-top: 18px;
  box-sizing: border-box;
  background: var(--surface-panel);
}

.signup-modal__location-input:hover {
  border-color: var(--border-strong);
}

.signup-modal__location-input:focus {
  border-color: var(--kiey-blue);
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.12);
}

.signup-modal__location-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 13px;
  line-height: 18px;
  color: #19A99D;
  font-weight: 500;
}

/* Los estilos base de result screen (.signup-modal__result, .result-icon,
   .result__title, .result__text, .result__link, .congrats-stores) viven
   ahora en signup-form.scss. Acá quedan sólo las variantes del wizard. */

/* ── Team picker — picker layout ──────────────────────────── */
/* Las clases base .team-picker* y los modificadores de result
   .signup-modal__result--picker / .result__title--compact /
   .result__text--compact viven ahora en signup-form.scss
   (compartidos con la pantalla "other team" del referral). */

/* ── Vendor-matched acknowledgement (request received) ────── */
.signup-modal__result--vendor-ack {
  padding-top: 0;
  padding-bottom: 0;
}

.signup-modal__result--vendor-ack .result-icon {
  margin-top: -24px;
}

.signup-modal__result--vendor-ack .result__text {
  margin-bottom: 8px;
}

.result__text--support {
  font-size: 13px;
  color: #64748B;
  margin-top: 4px;
}

.result__link {
  color: #2F6FED;
  text-decoration: none;
  font-weight: 600;
}

.result__link:hover {
  text-decoration: underline;
}

/* Center the footer button under vendor-ack, narrower than default */
.signup-modal__result--vendor-ack + .signup-modal__footer,
.signup-modal__result--vendor-ack ~ .signup-modal__footer {
  justify-content: center;
  padding-top: 16px;
}

.signup-modal__result--vendor-ack + .signup-modal__footer .signup-btn--lg,
.signup-modal__result--vendor-ack ~ .signup-modal__footer .signup-btn--lg {
  width: 220px;
  min-width: 0;
  height: 44px;
  font-size: 14px;
  margin: 0 auto;
}

/* ── Confirmed added: app download buttons ───────────────── */
/* .congrats-stores* vive ahora en signup-form.scss (compartido). */
.signup-modal__result--congrats {
  padding-bottom: 8px;
}

/* ── Waitlist v2 (compact) ──────────────────────────────── */
/* Las clases base .waitlist-v2* viven ahora en signup-form.scss
   (compartidas con la pantalla de waitlist del referral). El
   modificador .signup-modal--waitlist (que toca la card y el body
   del shell) vive en SignUpModalShell.vue. Acá quedan el accent
   strip y el botón de cierre claro, que el wizard renderiza vía el
   slot #header. */
.signup-modal__accent {
  height: 5px;
  width: 100%;
  background: linear-gradient(90deg, #2563EB 0%, #3B82F6 100%);
}

.signup-modal__close--light {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #F8FAFC;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;
  z-index: 2;
}

.signup-modal__close--light:hover {
  background: #EFF6FF;
  border-color: #BFDBFE;
}

/* Las reglas responsive de .waitlist-v2* viven en signup-form.scss.
   Acá queda sólo la del botón de cierre claro propio del wizard. */
@media (max-width: 600px) {
  .signup-modal__close--light {
    top: 14px;
    right: 14px;
    width: 36px;
    height: 36px;
  }
}

/* ── Info panel (dead end) ──────────────────────────────── */
.signup-modal__info-panel {
  text-align: center;
  padding: 20px 16px 14px;
}

.info-panel__icon-wrap {
  width: 48px; height: 48px;
  border-radius: 14px;
  background: var(--surface-tint);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.info-panel__title {
  font-weight: 700;
  font-size: 18px;
  line-height: 1.2;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.info-panel__text {
  font-size: 13px;
  line-height: 1.4;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.info-panel__text:last-of-type {
  margin-bottom: 0;
}

.info-panel__link {
  color: var(--text-link);
  text-decoration: none;
  font-weight: 600;
}

.info-panel__link:hover { text-decoration: underline; }

/* ── Footer ─────────────────────────────────────────────── */
/* .signup-modal__footer y .signup-btn* viven ahora en signup-form.scss. */
.signup-modal__helper {
  font-size: 14px;
  line-height: 1.4;
  color: #8c98b0;
}

/* ── Responsive ─────────────────────────────────────────── */
/* Las reglas responsive de la card, el header, el body, los campos,
   el consentimiento, los botones y el footer viven en el shell y en
   signup-form.scss. Acá quedan sólo las del contenido propio del
   wizard (role cards, decision cards, info panel, team picker). */
@media (max-width: 600px) {
  .signup-modal__login {
    padding: 7px 11px;
    font-size: 12px;
  }

  .signup-modal__title {
    font-size: 18px;
  }

  .signup-modal__title--center {
    font-size: 20px;
  }

  .signup-modal__supporting {
    font-size: 13px;
    margin-bottom: 14px;
  }

  .role-card {
    min-height: 64px;
    padding: 10px 12px;
  }

  .role-card__icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }

  .role-card__title {
    font-size: 15px;
  }

  .role-card__desc {
    font-size: 12px;
  }

  .decision-cards {
    flex-direction: column;
    gap: 10px;
  }

  .decision-card {
    min-height: 68px;
  }

  .signup-modal__info-panel {
    padding: 20px 16px 14px;
  }
}

/* Extra-narrow phones (iPhone SE-era, ~360px) */
@media (max-width: 380px) {
  .signup-modal__title {
    font-size: 17px;
  }

  .signup-modal__title--center {
    font-size: 18px;
  }

  .signup-modal__supporting {
    font-size: 12.5px;
    margin-bottom: 12px;
  }

  .role-card {
    min-height: 60px;
    gap: 10px;
    padding: 10px;
  }

  .role-card__icon {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 10px;
  }

  .role-card__icon svg {
    width: 18px;
    height: 18px;
  }

  .role-card__title {
    font-size: 14px;
  }

  .role-card__desc {
    font-size: 11.5px;
  }

  .decision-card {
    min-height: 64px;
    padding: 12px 10px;
  }

  .decision-card__primary {
    font-size: 17px;
  }

  .decision-card__secondary {
    font-size: 12px;
  }

  .info-panel__title {
    font-size: 16px;
  }

  .info-panel__text {
    font-size: 12.5px;
  }
}
</style>
